import { IngredientLabel } from '@portal/types/studio/studio';
import { Fragment } from 'react';
import { Typography } from '@ag1/ui';
import CustomPortableText from '@portal/shared/studio/PortableText/PortableText';
import styles from './IngredientModalEN.module.scss';

interface NutritionItem {
  _key?: string;
  _id?: string;
  isLineBreak?: boolean;
  name?: string;
  value?: string;
  percentage?: string;
  thickness?: string;
}

type IngredientLabelDetails = NonNullable<IngredientLabel['ingredientLabelDetails']>;

interface IngredientLabelDetailProps {
  props: IngredientLabelDetails;
}

export const IngredientModalEN = ({ props }: IngredientLabelDetailProps) => {
  const getDividerClassName = (thickness: string | undefined) => {
    if (thickness === 'thick') {
      return styles.dividerThick;
    }
    if (thickness === 'thin') {
      return styles.divider;
    }
    return '';
  };

  const isFirstNutritionDescriptionItemMinimal =
    props?.nutritionDescription &&
    props.nutritionDescription.length > 0 &&
    !props.nutritionDescription[0].name &&
    !props.nutritionDescription[0].percentage;

  return (
    <>
      {props && (
        <div className={styles.modalContainer}>
          <div className={styles.logo_section}>
            <div className={styles.serving_info}>
              <p className={styles.serving_info_p}>Serving Size: {props.servingSize}</p>
            </div>
            <div className={styles.serving_info}>
              <p className={styles.serving_info_p}>Servings Per Container:</p>
              <p className={styles.serving_info_p}>{props.servingPerContainer}</p>
            </div>
          </div>

          <div className={styles.nutrition_facts}>
            {props.displayNutritionSubtitle && (
              <div
                className={`${styles.nutrition_facts_subTitle} ${
                  styles.right
                } ${getDividerClassName(props.nutritionSubtitleDividerThickness)}`}>
                <p className={`${styles.bold} ${styles.right}`}>Amount Per Serving</p>
                <p className={`${styles.bold} ${styles.right}`}>% DV</p>
              </div>
            )}
            {props?.nutrition?.map(
              (
                nutritionItem: NutritionItem,
                docIndex: number,
                allNutritionDocs: NutritionItem[]
              ) => {
                const {
                  _key: itemKey,
                  _id: itemId,
                  isLineBreak,
                  name,
                  value,
                  percentage,
                  thickness
                } = nutritionItem;

                const isFact = !isLineBreak;
                // const currentIsLineBreak = isLineBreak; // Renamed to avoid confusion if isLineBreak was in outer scope

                let shouldRenderDividerAfterFact = false;
                if (isFact) {
                  shouldRenderDividerAfterFact = true; // Default to true for facts
                  const nextNutritionDocument: NutritionItem | undefined =
                    allNutritionDocs[docIndex + 1];
                  if (nextNutritionDocument && nextNutritionDocument.isLineBreak) {
                    shouldRenderDividerAfterFact = false;
                  }
                }

                return (
                  <Fragment key={itemKey || itemId || `nut-en-${docIndex}`}>
                    {isFact && (
                      <>
                        <div className={styles.nutrition_fact}>
                          <p className={`${styles.nutrition_name} ${styles.elem}`}>{name}</p>
                          <div className={styles.row}>
                            <p className={styles.elem}>{value}</p>
                            {percentage ? (
                              <p className={styles.elem}>{percentage}</p>
                            ) : (
                              <p className={styles.elem}>&nbsp;</p>
                            )}
                          </div>
                        </div>
                        {shouldRenderDividerAfterFact && <div className={styles.divider} />}
                      </>
                    )}
                    {isLineBreak && (
                      <div
                        className={thickness === 'thick' ? styles.dividerThick : styles.divider}
                      />
                    )}
                  </Fragment>
                );
              }
            )}
          </div>

          <div
            className={`${styles.nutrition_des} ${
              isFirstNutritionDescriptionItemMinimal ? styles.smallGap : ''
            }`}>
            {props.nutritionDescriptionDisplayHeader && (
              <div
                className={`${styles.nutrition_facts_subTitle} ${
                  styles.right
                } ${getDividerClassName(props.nutritionDescriptionHeaderDividerThickness)}`}>
                <p className={`${styles.bold} ${styles.right}`}>Amount Per Serving</p>
                <p className={styles.elem}>&nbsp;</p>
              </div>
            )}
            {props?.nutritionDescription?.map((e, i) => {
              return (
                <div key={e._key || `nutrition-desc-${i}`}>
                  <div className={`${styles.nutrition_des_fact} ${styles.row}`}>
                    {e.name && (
                      <p
                        className={`${styles.nutrition_name} ${styles.bold} ${styles.small} ${styles.elem}`}>
                        {e.name}
                      </p>
                    )}
                    {e.percentage && (
                      <div className={styles.row}>
                        <p className={`${styles.elem} ${styles.bold}`}>{e.value}</p>
                        {e.percentage ? (
                          <p className={styles.elem}>{e.percentage}</p>
                        ) : (
                          <p className={styles.elem}>&nbsp;</p>
                        )}
                      </div>
                    )}
                  </div>
                  {e?.description && (
                    <Typography
                      variant="body"
                      component="div"
                      size="2"
                      className={styles.nutrition_detail}>
                      <CustomPortableText value={e.description} />
                    </Typography>
                  )}
                  {e.dividerThickness !== 'hidden' && (
                    <div className={getDividerClassName(e.dividerThickness)} />
                  )}
                </div>
              );
            })}
            {props?.otherIngredient && (
              <div className={`${styles.otherIngredient}`}>
                <Typography variant="body" component="div" size="2" className={styles.bold}>
                  <CustomPortableText
                    value={props.otherIngredient.title ? props.otherIngredient.title : []}
                  />
                </Typography>
                <Typography
                  variant="body"
                  component="div"
                  size="2"
                  className={styles.otherIngredient_detail}>
                  <CustomPortableText
                    value={
                      props.otherIngredient.description ? props.otherIngredient.description : []
                    }
                  />
                </Typography>
              </div>
            )}
            {props?.contains && (
              <div>
                <div className={`${styles.contain} ${styles.small}`}>
                  <Typography
                    variant="body"
                    component="div"
                    size="2"
                    className={`${styles.contain_title} ${styles.bold}`}>
                    Contains:
                  </Typography>
                  <Typography
                    variant="body"
                    component="div"
                    size="2"
                    className={styles.contain_detail}>
                    <CustomPortableText value={props.contains.containsIngredient ?? []} />
                  </Typography>
                </div>
                <Typography
                  variant="body"
                  component="div"
                  size="2"
                  className={`${styles.contain_des} ${styles.small}`}>
                  <CustomPortableText value={props.contains.description ?? []} />
                </Typography>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default IngredientModalEN;
